import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  useDeleteOfferImageMutation,
  useGetOfferImagesQuery,
  useRemoveOfferMainImageMutation,
  useSetOfferMainImageMutation,
  useUploadOfferImagesMutation,
} from '../../Service/extendedOffersApi';
import { getImageDisplayUrl } from '../../../Images/Utils/imageUrlHelper';
import FileUploadInput from '../../../Shared/Components/FileUpload/FileUploadInput';
import Status from '../../../Shared/Components/Status/Status';
import {
  MAX_OFFER_IMAGE_UPLOAD_COUNT,
  validateOfferImageSelection,
} from './offerImageUploadValidation';
import { extractApiErrorMessage } from '../../../../Utils/apiErrorUtils';

interface OfferImagesSectionProps {
  offerId: number;
  currentMainImageId?: string | null;
}

const OfferImagesSection = ({ offerId, currentMainImageId }: OfferImagesSectionProps) => {
  const {
    data: images = [],
    isLoading: isImagesLoading,
    isError: isImagesError,
    error: imagesError,
    refetch: refetchImages,
  } = useGetOfferImagesQuery(offerId, { skip: !offerId });

  const [deleteImage] = useDeleteOfferImageMutation();
  const [setMainImage, setMainImageStatus] = useSetOfferMainImageMutation();
  const [removeMainImage, removeMainImageStatus] = useRemoveOfferMainImageMutation();
  const [uploadImages, uploadStatus] = useUploadOfferImagesMutation();

  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      previews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previews]);

  const handleFilesSelected = (selectedFileList: FileList) => {
    const selectedFiles = Array.from(selectedFileList);

    const validationResult = validateOfferImageSelection(files, selectedFiles);
    if (!validationResult.isValid) {
      setValidationError(validationResult.error);
      return;
    }

    setValidationError(null);
    setFiles((prev) => [...prev, ...selectedFiles]);
    const newPreviews = selectedFiles.map((file) => URL.createObjectURL(file));
    setPreviews((prev) => [...prev, ...newPreviews]);
  };

  const handleRemovePreview = (index: number) => {
    URL.revokeObjectURL(previews[index]);
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    setValidationError(null);
  };

  const handleUpload = async () => {
    if (!files.length) return;
    try {
      setValidationError(null);
      await uploadImages({ offerId, files }).unwrap();
      setFiles([]);
      setPreviews([]);
      refetchImages();
    } catch (err) {
      // error displayed via Status
    }
  };

  const handleDelete = async (imageId: string) => {
    try {
      await deleteImage({ imageId, offerId }).unwrap();
      refetchImages();
    } catch (err) {
      // error displayed via Status
    }
  };

  const handleSetMainImage = async (imageId: string) => {
    try {
      await setMainImage({ offerId, imageId }).unwrap();
      refetchImages();
    } catch (err) {
      // error displayed via Status
    }
  };

  const handleRemoveMainImage = async () => {
    try {
      await removeMainImage(offerId).unwrap();
      refetchImages();
    } catch (err) {
      // error displayed via Status
    }
  };

  const effectiveMainImageId = currentMainImageId;

  return (
    <Box>
      {/* Gallery */}
      <Typography variant="subtitle1" fontWeight={500} gutterBottom>
        Wgrane zdjęcia:
      </Typography>

      {isImagesLoading && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" width={130} height={130} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      )}

      {isImagesError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {extractApiErrorMessage(imagesError, 'Nie udało się załadować zdjęć oferty.')}
        </Typography>
      )}

      {!isImagesLoading && images.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Brak zdjęć. Wgraj pierwsze zdjęcia poniżej.
        </Typography>
      )}

      {images.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          {images.map((img) => {
            const isMain = img.id === effectiveMainImageId;
            return (
              <Box
                key={img.id}
                sx={{
                  position: 'relative',
                  width: 130,
                  height: 130,
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: isMain
                    ? '0 0 0 3px #1976d2'
                    : '0 0 4px rgba(0,0,0,0.2)',
                  transition: 'box-shadow 0.2s',
                }}
              >
                <img
                  src={getImageDisplayUrl(img)}
                  alt={img.altText || `offer-image-${img.id}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />

                {isMain && (
                  <Chip
                    icon={<StarIcon />}
                    label="Główne"
                    size="small"
                    color="primary"
                    sx={{ position: 'absolute', top: 4, left: 4, fontSize: '0.7rem' }}
                  />
                )}

                {/* Set / remove main image */}
                <Tooltip title={isMain ? 'Usuń jako zdjęcie główne' : 'Ustaw jako zdjęcie główne'}>
                  <IconButton
                    size="small"
                    onClick={() => (isMain ? handleRemoveMainImage() : handleSetMainImage(img.id))}
                    sx={{
                      position: 'absolute',
                      bottom: 2,
                      left: 2,
                      backgroundColor: 'rgba(255,255,255,0.8)',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                    }}
                  >
                    {isMain ? (
                      <StarIcon fontSize="small" color="primary" />
                    ) : (
                      <StarBorderIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>

                {/* Delete */}
                <IconButton
                  size="small"
                  onClick={() => handleDelete(img.id)}
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      <Status
        isLoading={setMainImageStatus.isLoading || removeMainImageStatus.isLoading}
        isError={setMainImageStatus.isError || removeMainImageStatus.isError}
        isSuccess={setMainImageStatus.isSuccess || removeMainImageStatus.isSuccess}
        error={setMainImageStatus.error ?? removeMainImageStatus.error}
      />

      {/* Upload form */}
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="subtitle1" fontWeight={500} gutterBottom>
          Dodaj zdjęcia:
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Możesz jednorazowo wgrać do <strong>{MAX_OFFER_IMAGE_UPLOAD_COUNT} zdjęć</strong> (każde maks. <strong>2 MB</strong>).
          Wybrano: <strong>{files.length}</strong> z {MAX_OFFER_IMAGE_UPLOAD_COUNT}.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FileUploadInput onFilesSelected={handleFilesSelected} />
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={uploadStatus.isLoading || !files.length}
            size="large"
          >
            {uploadStatus.isLoading ? <CircularProgress size={20} color="inherit" /> : 'Wyślij zdjęcia'}
          </Button>
        </Box>

        {validationError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {validationError}
          </Alert>
        )}

        {previews.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2, gap: 1 }}>
            {previews.map((src, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  width: 100,
                  height: 100,
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 0 4px rgba(0,0,0,0.2)',
                }}
              >
                <img
                  src={src}
                  alt={`preview-${index}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemovePreview(index)}
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        <Status
          isLoading={uploadStatus.isLoading}
          isError={uploadStatus.isError}
          isSuccess={uploadStatus.isSuccess}
          error={uploadStatus.error}
        />
      </Paper>
    </Box>
  );
};

export default OfferImagesSection;
